import { useMemo } from "react";
import {
  backtestSummaryFromNav,
  resolveNavPeriodBounds,
  getSignalSetFrequencyMetrics,
} from "./backtestMetrics";

const EMPTY_METRICS_ROW = {
  backtestPeriod: "—",
  days: "—",
  totalReturn: "0%",
  annualized: "0% annualized",
  sharpeRatio: "0",
  maxDrawdown: "0%",
  drawdownDuration: "0 days duration",
};

function formatBacktestDaysLabel(startDate, endDate) {
  if (!startDate || !endDate) return "—";
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const years = (diffDays / 365.25).toFixed(2);
  return `${diffDays} days (${years} years)`;
}

/**
 * Custom hook to calculate strategy performance metrics
 */
export const useStrategyMetrics = ({
  strategyVal,
  historicalSignalSet,
  period,
  customFromDate,
  customToDate,
  appliedCustomFromDate,
  appliedCustomToDate,
}) => {
  // Calculate backtest metrics from NAV series using backtestSummaryFromNav
  // ALWAYS use original, non-rebased strategyNavSeries for metrics (independent of period filters)
  const calculatedMetrics = useMemo(() => {
    if (!strategyVal || !historicalSignalSet?.navs?.strategyNavSeries) {
      return null;
    }

    try {
      const strategyNavSeriesData = historicalSignalSet.navs.strategyNavSeries;
      if (!strategyNavSeriesData) return null;

      const { startDate, endDate } = resolveNavPeriodBounds(
        strategyNavSeriesData,
        period,
        customFromDate,
        customToDate,
        appliedCustomFromDate,
        appliedCustomToDate
      );

      const navSeries = new Map(
        Object.entries(strategyNavSeriesData).map(([date, nav]) => [date, nav])
      );

      if (!startDate || !endDate || !navSeries || navSeries.size === 0)
        return null;

      // Optional configuration
      const options = {
        tradingDays: 252,
        riskFreeRateAnnual: 0.02,
        ddof: 1,
        sortinoThreshold: 0,
        ignoreNonTradingDays: true,
        mode: "calendar",
      };

      return backtestSummaryFromNav(navSeries, startDate, endDate, options);
    } catch (error) {
      console.error("Error calculating backtest metrics:", error);
      return null;
    }
  }, [
    strategyVal,
    historicalSignalSet,
    period,
    customFromDate,
    customToDate,
    appliedCustomFromDate,
    appliedCustomToDate,
  ]);

  // Calculate metric data for display
  const metricData = useMemo(() => {
    const bounds = resolveNavPeriodBounds(
      historicalSignalSet?.navs?.strategyNavSeries,
      period,
      customFromDate,
      customToDate,
      appliedCustomFromDate,
      appliedCustomToDate
    );

    const freqMetrics =
      strategyVal && bounds.startDate && bounds.endDate
        ? getSignalSetFrequencyMetrics(
            historicalSignalSet?.signalSets,
            bounds.startDate,
            bounds.endDate
          )
        : null;

    const signalSetFrequencyDays =
      freqMetrics?.averageRounded != null
        ? String(freqMetrics.averageRounded)
        : "—";
    const signalSetTradingDays = freqMetrics?.totalSignalsInPeriod ?? 0;
    const signalCalendarYmds = freqMetrics?.signalCalendarYmds ?? [];
    const signalSetsByCalendarYmd = freqMetrics?.signalsByYmd ?? {};
    const signalCalendarPeriodStartYmd = bounds.startDate ?? "";
    const signalCalendarPeriodEndYmd = bounds.endDate ?? "";
    const frequencyFields = {
      signalSetFrequencyDays,
      signalSetTradingDays,
      signalCalendarYmds,
      signalSetsByCalendarYmd,
      signalCalendarPeriodStartYmd,
      signalCalendarPeriodEndYmd,
    };

    if (!strategyVal) {
      return {
        ...EMPTY_METRICS_ROW,
        signalSetFrequencyDays: "—",
        signalSetTradingDays: 0,
        signalCalendarYmds: [],
        signalSetsByCalendarYmd: {},
        signalCalendarPeriodStartYmd: "",
        signalCalendarPeriodEndYmd: "",
      };
    }

    // Use calculatedMetrics if available (calculated from selected benchmark/period)
    if (calculatedMetrics) {
      const backtest = calculatedMetrics.backtest || {};
      const startDate = backtest.effectiveStartDate || backtest.startDate || "";
      const endDate = backtest.effectiveEndDate || backtest.endDate || "";
      const backtestPeriod =
        startDate && endDate ? `${startDate} to ${endDate}` : "—";
      const days = formatBacktestDaysLabel(startDate, endDate);

      // Format total return
      const totalReturn =
        calculatedMetrics.totalTimeWeightedReturnPct !== undefined &&
        !isNaN(calculatedMetrics.totalTimeWeightedReturnPct)
          ? `${calculatedMetrics.totalTimeWeightedReturnPct.toFixed(2)}%`
          : "0%";

      // Format annualized return
      const annualized =
        calculatedMetrics.annualizedReturnPct !== undefined &&
        !isNaN(calculatedMetrics.annualizedReturnPct)
          ? `${calculatedMetrics.annualizedReturnPct.toFixed(2)}% annualized`
          : "0% annualized";

      // Format Sharpe ratio
      const sharpeRatio =
        calculatedMetrics.sharpe !== undefined &&
        !isNaN(calculatedMetrics.sharpe)
          ? calculatedMetrics.sharpe.toFixed(2)
          : "0";

      // Format max drawdown
      const worstDrawdown = calculatedMetrics.worstDrawdown || {};
      const maxDrawdown =
        worstDrawdown.drawdownPct !== undefined &&
        !isNaN(worstDrawdown.drawdownPct)
          ? `${worstDrawdown.drawdownPct.toFixed(2)}%`
          : "0%";

      // Format drawdown duration
      const drawdownDuration =
        worstDrawdown.daysFromStartToEnd !== undefined &&
        worstDrawdown.daysFromStartToEnd !== null
          ? `${worstDrawdown.daysFromStartToEnd} days duration`
          : "0 days duration";

      return {
        backtestPeriod,
        days,
        totalReturn,
        annualized,
        sharpeRatio,
        maxDrawdown,
        drawdownDuration,
        ...frequencyFields,
      };
    }

    // Fallback to backend data if calculatedMetrics is not available
    if (!historicalSignalSet?.backtestSummary) {
      return { ...EMPTY_METRICS_ROW, ...frequencyFields };
    }

    const summary = historicalSignalSet.backtestSummary;
    const backtest = summary.backtest || {};

    // Format dates
    const startDate = backtest.startDate || backtest.effectiveStartDate || "";
    const endDate = backtest.endDate || backtest.effectiveEndDate || "";
    const backtestPeriod =
      startDate && endDate ? `${startDate} to ${endDate}` : "—";
    const days = formatBacktestDaysLabel(startDate, endDate);

    // Format total return
    const totalReturn =
      summary.totalTimeWeightedReturnPct !== undefined
        ? `${summary.totalTimeWeightedReturnPct.toFixed(2)}%`
        : "0%";

    // Format annualized return
    const annualized =
      summary.annualizedReturnPct !== undefined
        ? `${summary.annualizedReturnPct.toFixed(2)}% annualized`
        : "0% annualized";

    // Format Sharpe ratio
    const sharpeRatio =
      summary.sharpe !== undefined ? summary.sharpe.toFixed(2) : "0";

    // Format max drawdown
    const worstDrawdown = summary.worstDrawdown || {};
    const maxDrawdown =
      worstDrawdown.drawdownPct !== undefined
        ? `${worstDrawdown.drawdownPct.toFixed(2)}%`
        : "0%";

    // Format drawdown duration
    const drawdownDuration =
      worstDrawdown.daysFromStartToEnd !== undefined
        ? `${worstDrawdown.daysFromStartToEnd} days duration`
        : "0 days duration";

    return {
      backtestPeriod,
      days,
      totalReturn,
      annualized,
      sharpeRatio,
      maxDrawdown,
      drawdownDuration,
      ...frequencyFields,
    };
  }, [
    strategyVal,
    calculatedMetrics,
    historicalSignalSet,
    period,
    customFromDate,
    customToDate,
    appliedCustomFromDate,
    appliedCustomToDate,
  ]);

  return { calculatedMetrics, metricData };
};

